import { createRoute } from '@tanstack/react-router'
import { projectRoute } from '../../route'
import { useState, useEffect, useCallback, lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { orpc } from '@/lib/orpc-client'
import { useSetPageSubtitle } from '@/lib/page-title-context'

const DrawingBoard = lazy(() =>
  import('@/components/drawings/drawing-board').then((mod) => ({ default: mod.DrawingBoard }))
)

export const drawDetailRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/draw/$drawingId',
  component: DrawingEditorPage,
})

function DrawingEditorPage() {
  const { projectId, drawingId } = drawDetailRoute.useParams()
  const { t } = useTranslation('documents')
  const [title, setTitle] = useState<string | undefined>()
  const [error, setError] = useState(false)

  const handleRename = useCallback(
    async (newName: string) => {
      await orpc.drawings.save({ projectId, drawingId, title: newName })
      setTitle(newName)
    },
    [projectId, drawingId]
  )

  useSetPageSubtitle(title ?? null, handleRename)

  useEffect(() => {
    let cancelled = false

    orpc.drawings
      .get({ projectId, drawingId })
      .then((res) => {
        if (cancelled) return
        setTitle(res.drawing.title)
      })
      .catch(() => {
        if (cancelled) return
        setError(true)
      })

    return () => {
      cancelled = true
    }
  }, [projectId, drawingId])

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-destructive">{t('drawings.loadError')}</p>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <Suspense
        fallback={
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <p className="text-muted-foreground">{t('drawings.loadingBoard')}</p>
            </div>
          </div>
        }
      >
        <DrawingBoard projectId={projectId} drawingId={drawingId} />
      </Suspense>
    </div>
  )
}
